import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../assets/styles/Contact.module.scss';
import { motion } from 'framer-motion';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { ROLE_MAP, BUDGET_MAP } from '../utils/constants';

const Contact: React.FC = () => {
  useScrollToTop();
  const navigate = useNavigate();

  // フォームの状態管理
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    position: '',
    budget: '',
    message: '',
    privacy: false
  });

  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 入力変更時のハンドラー
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => {
      const newData = { ...prev, [name]: type === 'checkbox' ? checked : value };

      // 入力値があるかどうかチェック（チェックボックス以外）
      const hasValue = Object.entries(newData).some(([key, val]) => {
        if (key === 'privacy') return false;
        return val !== '';
      });

      setIsDirty(hasValue);
      return newData;
    });
  };

  // ページ離脱時の確認（リロード/タブ閉じ）
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // 内部ナビゲーションの確認
  const handleNavigation = (path: string) => {
    if (isDirty) {
      if (window.confirm('入力途中の内容が消えてしまいます。本当によろしいですか？')) {
        navigate(path);
      }
    } else {
      navigate(path);
    }
  };

  const handleExternalLink = (url: string) => {
    // 別タブで開くリンクなどは確認不要
    window.open(url, '_blank');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/public/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        throw new Error('Failed to submit contact request');
      }

      const data = await res.json();
      
      // 送信完了時は dirty フラグを解除して遷移
      setIsDirty(false);
      navigate('/contact/thanks');
    } catch (error) {
      console.error('送信エラー:', error);
      alert('お問い合わせの送信に失敗しました。しばらく経ってから再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.containerWrap}>
      <header className={styles.header}>
        <div className={styles.logoArea} onClick={() => handleNavigation('/')}>
          <img src="/images/logo_aibex_line_b.svg" alt="Aibex" className={styles.logo} />
          <span className={styles.logoKatakana}>マーケティングに特化したAIエージェント「Aibex（アイベックス）」</span>
        </div>
      </header>

      <div className={styles.backgroundGlow}>
        <div className={styles.glowBlue} />
        <div className={styles.glowPink} />
      </div>

      <main className={styles.main}>
        <div className={styles.backLinkContainer}>
          <span className={styles.backLink} onClick={() => handleNavigation('/')}>
            ← トップページへ戻る
          </span>
        </div>
        <motion.div
          className={styles.titleArea}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1>お問い合わせ</h1>
          <p>
            Aibex導入のご相談から、完全運用代行のご依頼、<br />
            広告代理店様によるお問い合わせなど、何でもお気軽にご相談ください。
          </p>
        </motion.div>

        <motion.div
          className={styles.formContainer}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label>会社名<span className={styles.required}>*</span></label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="株式会社Inovis"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>氏名<span className={styles.required}>*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="山田 太郎"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>メールアドレス<span className={styles.required}>*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@inovis.co.jp"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>つながりやすい電話番号<span className={styles.required}>*</span></label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="03-1234-5678"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>貴社の立場を教えてください<span className={styles.required}>*</span></label>
              <select
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
              >
                <option value="" disabled>選択してください</option>
                {Object.entries(ROLE_MAP).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>月間の広告ご予算<span className={styles.required}>*</span></label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                required
              >
                <option value="" disabled>選択してください</option>
                {Object.entries(BUDGET_MAP).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Aibexに関するご相談・ご要望</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Aibexのデモが見たい、成果など事例を知りたい、サービス提携したい、などご要望・ご相談事項があればこちらに記入してください。"
              ></textarea>
            </div>

            <div className={styles.checkboxGroup}>
              <input
                type="checkbox"
                id="privacy"
                name="privacy"
                checked={formData.privacy}
                onChange={handleChange}
                required
              />
              <label htmlFor="privacy">
                <span onClick={() => handleExternalLink('/privacy-policy')}>プライバシーポリシー</span>をご確認の上、同意して送信してください。
              </label>
            </div>

            <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
              {isSubmitting ? '送信中...' : 'お問い合わせを送信する'}
            </button>
          </form>
        </motion.div>
      </main>

      <footer className={styles.footer}>
        <img src="/images/logo_aibex_line_b.svg" alt="Aibex" className={styles.footerLogo} />
        <div className={styles.footerLinks}>
          <a href="https://inovis.co.jp" target="_blank" rel="noopener noreferrer">運営会社</a>
          <span onClick={() => handleNavigation('/privacy-policy')}>プライバシーポリシー</span>
          <span onClick={() => handleNavigation('/terms')}>利用規約</span>
        </div>
        <p className={styles.copyright}>&copy; 2026 Inovis Inc. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Contact;
